import { useState } from 'react'
import { Brain, Building2, Plus, Truck } from 'lucide-react'
import { useAuth } from '@/auth'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  AdminGestionDialog,
  ConsumoIAFiltersBar,
  ConsumoIATable,
  ResumenProveedorCard,
} from '../components'
import { useConsumoIA, useCreateConsumoIA, useResumenProveedor, useUpdateConsumoIA } from '../hooks/useConsumoIA'
import type { ConsumoIA, ConsumoIAFilters } from '../types/consumoIA'

export function ConsumoIAPage() {
  const { user } = useAuth()
  const isAdmin = user?.is_superuser || user?.groups?.includes('admin') || false

  const [filters, setFilters] = useState<ConsumoIAFilters>({
    anio: new Date().getFullYear(),
    mes: new Date().getMonth() + 1,
  })
  const [dialogOpen, setDialogOpen] = useState(false)
  const [selectedRecord, setSelectedRecord] = useState<ConsumoIA | null>(null)
  const [viewMode, setViewMode] = useState<'laboratorio' | 'proveedor'>('laboratorio')

  const { data: registros = [], isLoading } = useConsumoIA(filters)
  const { data: resumen, isLoading: isLoadingResumen } = useResumenProveedor(filters, isAdmin)

  const filtersBase = { anio: filters.anio, mes: filters.mes }
  const { data: registrosBase = [] } = useConsumoIA(filtersBase)
  const paisesDisponibles = [...new Set(registrosBase.map((r) => r.pais))]

  const createMutation = useCreateConsumoIA()
  const updateMutation = useUpdateConsumoIA()

  const handleOpenCreate = () => {
    setSelectedRecord(null)
    setDialogOpen(true)
  }

  const handleOpenEdit = (record: ConsumoIA) => {
    setSelectedRecord(record)
    setDialogOpen(true)
  }

  const handleSubmit = async (data: any, id?: number) => {
    if (id) {
      await updateMutation.mutateAsync({ id, data })
    } else {
      await createMutation.mutateAsync(data)
    }
    setDialogOpen(false)
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="rounded-lg bg-violet-500/10 p-2">
            <Brain className="h-6 w-6 text-violet-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-black dark:text-white">Consumo IA</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Procesamiento de imágenes por laboratorio
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 gap-1">
              <button
                onClick={() => setViewMode('laboratorio')}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                  viewMode === 'laboratorio'
                    ? 'bg-white dark:bg-gray-700 text-black dark:text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                )}
              >
                <Building2 className="h-3.5 w-3.5" />
                Laboratorio
              </button>
              <button
                onClick={() => setViewMode('proveedor')}
                className={cn(
                  'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all',
                  viewMode === 'proveedor'
                    ? 'bg-white dark:bg-gray-700 text-black dark:text-white shadow-sm'
                    : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                )}
              >
                <Truck className="h-3.5 w-3.5" />
                Proveedor
              </button>
            </div>
          )}
          {isAdmin && (
            <Button
              onClick={handleOpenCreate}
              className="bg-violet-800 hover:bg-violet-900 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Nuevo registro
            </Button>
          )}
        </div>
      </div>

      {isAdmin && (
        <>
          {isLoadingResumen ? (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-20" />
              ))}
            </div>
          ) : resumen ? (
            <ResumenProveedorCard resumen={resumen} registros={registros} viewMode={viewMode} />
          ) : null}
        </>
      )}

      <ConsumoIAFiltersBar filters={filters} onFiltersChange={setFilters} paisesDisponibles={paisesDisponibles} />

      <Card className="border border-gray-300 dark:border-gray-700">
        <CardContent className="p-0">
          <ConsumoIATable
            registros={registros}
            isLoading={isLoading}
            onEdit={isAdmin ? handleOpenEdit : undefined}
            viewMode={isAdmin ? viewMode : 'laboratorio'}
          />
        </CardContent>
      </Card>

      {isAdmin && (
        <AdminGestionDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          record={selectedRecord}
          onSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          viewMode={viewMode}
        />
      )}
    </div>
  )
}
